import { mergeStyles, plural } from '@cian/utils';
import get from 'lodash/get';
import * as React from 'react';

import styles from './VillageObjects.css';
import { generateLinkText } from './helpers';
import { VillageObjectTabPane } from './tab_pane';
import { trackAllObjectsLinkClick } from './tab_pane/tracking';
import { NavigationLinkTypes } from '../../constants/navigation';
import { NEW_OBJECTS_KEYS, NEW_OBJECTS_NAMES, NEW_OBJECT_VARIANTS } from '../../interfaces/village';
import { trackObjectTabClick } from '../../tracking';
import { INewobjectCategorySchema } from '../../types';

export interface IStoreProps {
  objects: TIndexableVillageObject;
  objectsOnSale: TIndexableVillageObjectCount | null;
  builderObjectsOnSale: TIndexableVillageObjectCount | null;
  resaleObjectsOnSale: TIndexableVillageObjectCount;
  totalOnSaleCount: number;
  totalOnSaleUrl?: string;
  isCroppedCard: boolean;
}

export interface IDispatchProps {
  setNavigationLink(): void;
}

type TProps = IStoreProps & IDispatchProps;

export interface IState {
  activeKey: string;
  activeObjectKeys: string[];
}

export type TIndexableVillageObject = Record<string, INewobjectCategorySchema | null>;
export type TIndexableVillageObjectCount = Record<string, number>;

export class VillageObjects extends React.Component<TProps, IState> {
  public state: IState = {
    activeKey: '',
    activeObjectKeys: [],
  };

  public constructor(props: TProps) {
    super(props);

    const { objects } = this.props;

    const activeObjectKeys = Object.keys(objects).filter(key => objects[key]);

    this.state = {
      activeKey: activeObjectKeys[0],
      activeObjectKeys,
    };
  }

  public componentDidMount() {
    const { totalOnSaleCount, totalOnSaleUrl } = this.props;
    const hasTotalObjectsLink = Boolean(totalOnSaleCount > 0 && totalOnSaleUrl !== '');

    if (this.state.activeObjectKeys.length || hasTotalObjectsLink) {
      this.props.setNavigationLink();
    }
  }

  public render() {
    const { activeKey, activeObjectKeys } = this.state;
    const {
      objects,
      objectsOnSale,
      builderObjectsOnSale,
      resaleObjectsOnSale,
      totalOnSaleCount,
      totalOnSaleUrl,
      isCroppedCard,
    } = this.props;
    const hasTotalObjectsLink = Boolean(totalOnSaleCount > 0 && totalOnSaleUrl !== '');
    if (!activeObjectKeys.length && !hasTotalObjectsLink) {
      return null;
    }

    const tabsContent = activeObjectKeys.map(key => {
      if (activeKey !== key) {
        return null;
      }
      const item = objects[key] as INewobjectCategorySchema;
      const linkText = generateLinkText(objectsOnSale, key);
      const builderLinkText = generateLinkText(builderObjectsOnSale, key);

      return (
        <VillageObjectTabPane
          key={`content_${key}`}
          item={item}
          isLandplot={key === NEW_OBJECTS_KEYS.LANDPLOT}
          link={linkText && get(item, 'seo.url', '')}
          linkText={linkText}
          builderLink={builderLinkText && get(item, 'builderSeo.url', '')}
          builderLinkText={builderLinkText}
          withResale={resaleObjectsOnSale[key] > 0}
          hidePriceByType={isCroppedCard}
        />
      );
    });

    return (
      <div
        {...mergeStyles(styles['container'], !activeObjectKeys.length && styles['empty_container'])}
        id={NavigationLinkTypes.Objects}
      >
        <div className={styles['title_wrapper']}>
          <h2 className={styles['title']}>Объекты в посёлке</h2>
          {totalOnSaleCount > 0 && totalOnSaleUrl !== '' && (
            <a
              className={styles['all_offers_link']}
              href={totalOnSaleUrl}
              target="_blank"
              onClick={trackAllObjectsLinkClick}
            >
              Посмотреть {`${totalOnSaleCount} ${plural(totalOnSaleCount, NEW_OBJECT_VARIANTS.totalOnSale)}`}
            </a>
          )}
        </div>
        {tabsContent && tabsContent.length > 0 && (
          <>
            <div className={styles['tab_links']}>
              {activeObjectKeys.map(key => (
                <div
                  key={`link_${key}`}
                  {...mergeStyles(styles['tab_link'], activeKey === key && styles['tab_link--active'])}
                  onClick={this.handleTabLinkClick(key)}
                >
                  {NEW_OBJECTS_NAMES[key]}
                </div>
              ))}
            </div>
            <div className={styles['tab_content']}>{tabsContent}</div>
          </>
        )}
      </div>
    );
  }

  private handleTabLinkClick = (key: string) => () => {
    this.setState({ activeKey: key });
    trackObjectTabClick(key);
  };
}
